import { LocationDto } from '@trucktrax/trucktrax-ts-common';

import { MapTypes } from '../types';

/**
 * Geographic and coordinate utility functions
 */

/**
 * Validates that latitude and longitude are finite numbers and not (0,0)
 */
export function isValidLatLng(lat: number | undefined, lng: number | undefined): boolean {
  if (lat == null || lng == null) {
    return false;
  }
  if (lat === 0 && lng === 0) {
    return false;
  }
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/**
 * Converts Google Maps LatLng objects to LocationDto format
 * Centralized utility to avoid duplication across map components
 */
export function mapsToLocationDto(
  mapsLatLng: google.maps.LatLngLiteral | google.maps.LatLng | undefined | null,
): LocationDto {
  if (!mapsLatLng) {
    return {
      latitude: 0,
      longitude: 0,
    };
  }

  if (typeof mapsLatLng?.lat === 'function') {
    const latLng = mapsLatLng as google.maps.LatLng;
    return {
      latitude: latLng.lat(),
      longitude: latLng.lng(),
    };
  }

  const latLngLiteral = mapsLatLng as google.maps.LatLngLiteral;
  return {
    latitude: latLngLiteral.lat,
    longitude: latLngLiteral.lng,
  } as LocationDto;
}

export function determineMapCurrentCenter(
  map: google.maps.Map | undefined,
): { lat: number; lng: number } | undefined {
  if (!map) {
    return undefined;
  }

  const center = map.getCenter();
  if (!center) {
    return undefined;
  }

  const lat = center?.lat();
  const lng = center?.lng();
  if (isValidLatLng(lat, lng)) {
    return { lat: lat!, lng: lng! };
  }

  return undefined;
}

export function determineMapType(map: google.maps.Map | undefined): MapTypes | undefined {
  if (!map) {
    return undefined;
  }

  const currentMapType = map.getMapTypeId() as MapTypes;
  return currentMapType;
}

export function extractLatLngFromPosition(position: any): { lat: number; lng: number } {
  return {
    lat: typeof position?.lat === 'function' ? position.lat() : (position?.lat ?? 0),
    lng: typeof position?.lng === 'function' ? position.lng() : (position?.lng ?? 0),
  };
}

/**
 * Converts LocationDto to simple lat/lng object
 * Handles both legacy (lat/lng) and current (latitude/longitude) property names
 * @param location LocationDto with either lat/lng or latitude/longitude
 * @returns Normalized { lat, lng } object with 0 as default for missing values
 */
export function locationToLatLng(location: LocationDto): { lat: number; lng: number } {
  return {
    lat: location.latitude ?? location.lat ?? 0,
    lng: location.longitude ?? location.lng ?? 0,
  };
}

/**
 * Converts simple lat/lng object to LocationDto
 * @param latLng Object with lat and lng properties
 * @returns LocationDto with latitude and longitude properties
 */
export function latLngToLocation(latLng: { lat: number; lng: number }): LocationDto {
  return {
    latitude: latLng.lat,
    longitude: latLng.lng,
  };
}

/**
 * Checks if a LocationDto has non-zero coordinates
 * Note: Treats (0,0) as invalid since it's commonly used as an uninitialized/default value
 * @param location LocationDto to validate
 * @returns true if location has defined non-zero coordinates
 */
export function hasValidCoordinates(location: LocationDto | undefined | null): boolean {
  if (!location) {
    return false;
  }

  const lat = location.latitude ?? location.lat;
  const lng = location.longitude ?? location.lng;

  return lat != null && lng != null && lat !== 0 && lng !== 0;
}

/**
 * Checks if a location has valid (non-zero) coordinates
 * Only rejects the (0,0) sentinel value; allows valid locations on equator or prime meridian
 * @param location LocationDto to validate
 * @returns true if location is not (0,0)
 */
export function isLocationValid(location?: LocationDto): boolean {
  const lat = location?.latitude ?? 0;
  const lng = location?.longitude ?? 0;
  return !(lat === 0 && lng === 0);
}

/**
 * Converts LocationDto to google.maps.LatLngLiteral
 * @param location LocationDto with latitude/longitude properties
 * @returns Google Maps LatLngLiteral object
 */
export function toLatLngLiteral(location?: LocationDto): google.maps.LatLngLiteral {
  return {
    lat: location?.latitude ?? 0,
    lng: location?.longitude ?? 0,
  };
}

/**
 * Converts google.maps.LatLngLiteral to LocationDto
 * @param position Google Maps LatLngLiteral object
 * @returns LocationDto with latitude/longitude properties
 */
export function fromLatLngLiteral(position: google.maps.LatLngLiteral): LocationDto {
  return {
    latitude: position.lat,
    longitude: position.lng,
  };
}
